import { Typography } from '@mui/joy';
import { Warning } from '@phosphor-icons/react';
import { useMemo } from 'react';

import { DEFAULT_PLACEHOLDER, NOT_AVAILABLE } from 'core/constants';
// Deep import on purpose: the core/elements barrel circles back through core/components
// (ActionsCell → AgGridTable), which breaks module init under jest.
import TextEllipsis from 'core/elements/TextEllipsis';
import { IInfoCardLine } from 'core/types';
import { Color, Level } from 'theme/enums';

import { LabelWrapper, LineWrapper, ValueWrapper } from './style';
import { getValueContent } from './utils';

interface IProps extends IInfoCardLine {
  hasBackground?: boolean;
  justifyContent?: 'space-between' | 'flex-start';
  truncateLabel?: boolean;
}

const LineView = ({
  label,
  value,
  hasBackground = false,
  justifyContent = 'space-between',
  required = false,
  requiredMessage,
  extra,
  sx,
  truncateLabel = false,
}: IProps) => {
  const valueToDisplay = useMemo(() => {
    if (!!value && value !== NOT_AVAILABLE) {
      return value;
    }

    if (required) {
      return (
        <Typography
          color={Color.DANGER}
          level={Level.BODY_LG}
          sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
        >
          <Warning size={20} />
          {requiredMessage && (
            <Typography level={Level.BODY_SM} color={Color.DANGER}>
              {requiredMessage}
            </Typography>
          )}
        </Typography>
      );
    }

    return DEFAULT_PLACEHOLDER;
  }, [value, required, requiredMessage]);

  return (
    <LineWrapper hasBackground={hasBackground} justifyContent={justifyContent} gap={0.5}>
      <LabelWrapper truncateLabel={truncateLabel}>
        {/* TextEllipsis only tooltips when the text genuinely overflows, so short labels
            stay hover-silent; inherit keeps the wrapper's label typography. */}
        {truncateLabel && label ? (
          <TextEllipsis
            content={label}
            // fontSize inherits (14px); pin lineHeight so TextEllipsis's default can't drift.
            lineHeight={21}
            sx={{ fontSize: 'inherit', fontWeight: 'inherit', color: 'inherit' }}
          />
        ) : (
          label
        )}
        {justifyContent === 'flex-start' && <>:</>}
      </LabelWrapper>
      <ValueWrapper sx={sx}>
        {getValueContent(valueToDisplay)}
        {extra}
      </ValueWrapper>
    </LineWrapper>
  );
};

export default LineView;
